/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetMySystemCredentialPathParameters,
  RevokeMySystemCredentialPathParameters,
  RotateMySystemCredentialPathParameters,
  SetMySystemCredentialPathParameters,
  TLHTTPValidationError,
  TLIntegrationsCredentialCreateRequest,
  TLIntegrationsCredentialRotateRequest,
  TLIntegrationsCredentialSummary,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Revoke My System Credential
 */
export const revokeMySystemCredential = (
  { sysRef }: RevokeMySystemCredentialPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/systems/${sysRef}/credential`, method: 'DELETE', signal }, options);
};

export const getRevokeMySystemCredentialQueryKey = ({ sysRef }: RevokeMySystemCredentialPathParameters) => {
  return ['DELETE', 'systems', sysRef, 'credential'] as const;
};

export const getRevokeMySystemCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof revokeMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: RevokeMySystemCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getRevokeMySystemCredentialQueryKey({ sysRef });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof revokeMySystemCredential>>> = ({ signal }) =>
    revokeMySystemCredential({ sysRef }, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof revokeMySystemCredential>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RevokeMySystemCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof revokeMySystemCredential>>>;
export type RevokeMySystemCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function useRevokeMySystemCredential<
  TData = Awaited<ReturnType<typeof revokeMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeMySystemCredentialPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeMySystemCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof revokeMySystemCredential>>,
          TError,
          Awaited<ReturnType<typeof revokeMySystemCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRevokeMySystemCredential<
  TData = Awaited<ReturnType<typeof revokeMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeMySystemCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeMySystemCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof revokeMySystemCredential>>,
          TError,
          Awaited<ReturnType<typeof revokeMySystemCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRevokeMySystemCredential<
  TData = Awaited<ReturnType<typeof revokeMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeMySystemCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Revoke My System Credential
 */

export function useRevokeMySystemCredential<
  TData = Awaited<ReturnType<typeof revokeMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: RevokeMySystemCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRevokeMySystemCredentialQueryOptions({ sysRef }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Revoke My System Credential
 */
export const prefetchRevokeMySystemCredentialQuery = async <
  TData = Awaited<ReturnType<typeof revokeMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: RevokeMySystemCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRevokeMySystemCredentialQueryOptions({ sysRef }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Revoke My System Credential
 */
export const invalidateRevokeMySystemCredential = async (
  queryClient: QueryClient,
  { sysRef }: RevokeMySystemCredentialPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getRevokeMySystemCredentialQueryKey({ sysRef }) }, options);

  return queryClient;
};

/**
 * @summary Get My System Credential
 */
export const getMySystemCredential = (
  { sysRef }: GetMySystemCredentialPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsCredentialSummary>(
    { url: `/systems/${sysRef}/credential`, method: 'GET', signal },
    options,
  );
};

export const getGetMySystemCredentialQueryKey = ({ sysRef }: GetMySystemCredentialPathParameters) => {
  return ['systems', sysRef, 'credential'] as const;
};

export const getGetMySystemCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof getMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: GetMySystemCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMySystemCredentialQueryKey({ sysRef });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMySystemCredential>>> = ({ signal }) =>
    getMySystemCredential({ sysRef }, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMySystemCredential>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMySystemCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof getMySystemCredential>>>;
export type GetMySystemCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function useGetMySystemCredential<
  TData = Awaited<ReturnType<typeof getMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetMySystemCredentialPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySystemCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMySystemCredential>>,
          TError,
          Awaited<ReturnType<typeof getMySystemCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMySystemCredential<
  TData = Awaited<ReturnType<typeof getMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetMySystemCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySystemCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMySystemCredential>>,
          TError,
          Awaited<ReturnType<typeof getMySystemCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMySystemCredential<
  TData = Awaited<ReturnType<typeof getMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetMySystemCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get My System Credential
 */

export function useGetMySystemCredential<
  TData = Awaited<ReturnType<typeof getMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: GetMySystemCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMySystemCredentialQueryOptions({ sysRef }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get My System Credential
 */
export const prefetchGetMySystemCredentialQuery = async <
  TData = Awaited<ReturnType<typeof getMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: GetMySystemCredentialPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetMySystemCredentialQueryOptions({ sysRef }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get My System Credential
 */
export const invalidateGetMySystemCredential = async (
  queryClient: QueryClient,
  { sysRef }: GetMySystemCredentialPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetMySystemCredentialQueryKey({ sysRef }) }, options);

  return queryClient;
};

/**
 * @summary Rotate My System Credential
 */
export const rotateMySystemCredential = (
  { sysRef }: RotateMySystemCredentialPathParameters,
  tLIntegrationsCredentialRotateRequest: BodyType<TLIntegrationsCredentialRotateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsCredentialSummary>(
    {
      url: `/systems/${sysRef}/credential`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLIntegrationsCredentialRotateRequest,
      signal,
    },
    options,
  );
};

export const getRotateMySystemCredentialQueryKey = (
  { sysRef }: RotateMySystemCredentialPathParameters,
  tLIntegrationsCredentialRotateRequest?: BodyType<TLIntegrationsCredentialRotateRequest>,
) => {
  return ['PATCH', 'systems', sysRef, 'credential', tLIntegrationsCredentialRotateRequest] as const;
};

export const getRotateMySystemCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof rotateMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: RotateMySystemCredentialPathParameters,
  tLIntegrationsCredentialRotateRequest: BodyType<TLIntegrationsCredentialRotateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rotateMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getRotateMySystemCredentialQueryKey({ sysRef }, tLIntegrationsCredentialRotateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof rotateMySystemCredential>>> = ({ signal }) =>
    rotateMySystemCredential({ sysRef }, tLIntegrationsCredentialRotateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof rotateMySystemCredential>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type RotateMySystemCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof rotateMySystemCredential>>>;
export type RotateMySystemCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function useRotateMySystemCredential<
  TData = Awaited<ReturnType<typeof rotateMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RotateMySystemCredentialPathParameters,
  tLIntegrationsCredentialRotateRequest: BodyType<TLIntegrationsCredentialRotateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof rotateMySystemCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof rotateMySystemCredential>>,
          TError,
          Awaited<ReturnType<typeof rotateMySystemCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRotateMySystemCredential<
  TData = Awaited<ReturnType<typeof rotateMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RotateMySystemCredentialPathParameters,
  tLIntegrationsCredentialRotateRequest: BodyType<TLIntegrationsCredentialRotateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rotateMySystemCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof rotateMySystemCredential>>,
          TError,
          Awaited<ReturnType<typeof rotateMySystemCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRotateMySystemCredential<
  TData = Awaited<ReturnType<typeof rotateMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RotateMySystemCredentialPathParameters,
  tLIntegrationsCredentialRotateRequest: BodyType<TLIntegrationsCredentialRotateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rotateMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Rotate My System Credential
 */

export function useRotateMySystemCredential<
  TData = Awaited<ReturnType<typeof rotateMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: RotateMySystemCredentialPathParameters,
  tLIntegrationsCredentialRotateRequest: BodyType<TLIntegrationsCredentialRotateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rotateMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRotateMySystemCredentialQueryOptions(
    { sysRef },
    tLIntegrationsCredentialRotateRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Rotate My System Credential
 */
export const prefetchRotateMySystemCredentialQuery = async <
  TData = Awaited<ReturnType<typeof rotateMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: RotateMySystemCredentialPathParameters,
  tLIntegrationsCredentialRotateRequest: BodyType<TLIntegrationsCredentialRotateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rotateMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRotateMySystemCredentialQueryOptions(
    { sysRef },
    tLIntegrationsCredentialRotateRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Rotate My System Credential
 */
export const invalidateRotateMySystemCredential = async (
  queryClient: QueryClient,
  { sysRef }: RotateMySystemCredentialPathParameters,
  tLIntegrationsCredentialRotateRequest: BodyType<TLIntegrationsCredentialRotateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRotateMySystemCredentialQueryKey({ sysRef }, tLIntegrationsCredentialRotateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Set My System Credential
 */
export const setMySystemCredential = (
  { sysRef }: SetMySystemCredentialPathParameters,
  tLIntegrationsCredentialCreateRequest: BodyType<TLIntegrationsCredentialCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLIntegrationsCredentialSummary>(
    {
      url: `/systems/${sysRef}/credential`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLIntegrationsCredentialCreateRequest,
      signal,
    },
    options,
  );
};

export const getSetMySystemCredentialQueryKey = (
  { sysRef }: SetMySystemCredentialPathParameters,
  tLIntegrationsCredentialCreateRequest?: BodyType<TLIntegrationsCredentialCreateRequest>,
) => {
  return ['POST', 'systems', sysRef, 'credential', tLIntegrationsCredentialCreateRequest] as const;
};

export const getSetMySystemCredentialQueryOptions = <
  TData = Awaited<ReturnType<typeof setMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: SetMySystemCredentialPathParameters,
  tLIntegrationsCredentialCreateRequest: BodyType<TLIntegrationsCredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSetMySystemCredentialQueryKey({ sysRef }, tLIntegrationsCredentialCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof setMySystemCredential>>> = ({ signal }) =>
    setMySystemCredential({ sysRef }, tLIntegrationsCredentialCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, enabled: sysRef !== null && sysRef !== undefined, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof setMySystemCredential>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SetMySystemCredentialQueryResult = NonNullable<Awaited<ReturnType<typeof setMySystemCredential>>>;
export type SetMySystemCredentialQueryError = ErrorType<TLHTTPValidationError>;

export function useSetMySystemCredential<
  TData = Awaited<ReturnType<typeof setMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SetMySystemCredentialPathParameters,
  tLIntegrationsCredentialCreateRequest: BodyType<TLIntegrationsCredentialCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof setMySystemCredential>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof setMySystemCredential>>,
          TError,
          Awaited<ReturnType<typeof setMySystemCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSetMySystemCredential<
  TData = Awaited<ReturnType<typeof setMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SetMySystemCredentialPathParameters,
  tLIntegrationsCredentialCreateRequest: BodyType<TLIntegrationsCredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setMySystemCredential>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof setMySystemCredential>>,
          TError,
          Awaited<ReturnType<typeof setMySystemCredential>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSetMySystemCredential<
  TData = Awaited<ReturnType<typeof setMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: SetMySystemCredentialPathParameters,
  tLIntegrationsCredentialCreateRequest: BodyType<TLIntegrationsCredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Set My System Credential
 */

export function useSetMySystemCredential<
  TData = Awaited<ReturnType<typeof setMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { sysRef }: SetMySystemCredentialPathParameters,
  tLIntegrationsCredentialCreateRequest: BodyType<TLIntegrationsCredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSetMySystemCredentialQueryOptions({ sysRef }, tLIntegrationsCredentialCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Set My System Credential
 */
export const prefetchSetMySystemCredentialQuery = async <
  TData = Awaited<ReturnType<typeof setMySystemCredential>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { sysRef }: SetMySystemCredentialPathParameters,
  tLIntegrationsCredentialCreateRequest: BodyType<TLIntegrationsCredentialCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof setMySystemCredential>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSetMySystemCredentialQueryOptions({ sysRef }, tLIntegrationsCredentialCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Set My System Credential
 */
export const invalidateSetMySystemCredential = async (
  queryClient: QueryClient,
  { sysRef }: SetMySystemCredentialPathParameters,
  tLIntegrationsCredentialCreateRequest: BodyType<TLIntegrationsCredentialCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getSetMySystemCredentialQueryKey({ sysRef }, tLIntegrationsCredentialCreateRequest) },
    options,
  );

  return queryClient;
};
